import { Stack, NumberInput, Select } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { FormField } from '@/components/ui/FormField';

export interface DepreciationFormValues {
  depreciable_cost: number | null;
  salvage_value: number | null;
  asset_life_months: number | null;
  depreciation_method: string | null;
  date_acquired: string | null;
}

interface DepreciationFormFieldsProps {
  values: DepreciationFormValues;
  errors?: Partial<Record<keyof DepreciationFormValues, string>>;
  onChange: <K extends keyof DepreciationFormValues>(field: K, value: DepreciationFormValues[K]) => void;
}

export const DEPRECIATION_METHODS = [
  { value: 'straight-line', label: 'Straight-Line' },
  { value: 'declining-balance', label: 'Declining Balance' },
  { value: 'double-declining', label: 'Double Declining Balance' },
  { value: 'sum-of-years', label: 'Sum of Years Digits' },
];

export function DepreciationFormFields({ values, errors = {}, onChange }: DepreciationFormFieldsProps) {
  return (
    <Stack gap="md">
      <FormField
        label="Depreciable Cost"
        error={errors.depreciable_cost}
        required
      >
        <NumberInput
          placeholder="0.00"
          prefix="$"
          decimalScale={2}
          description="Cost basis for depreciation (usually purchase price)"
          value={values.depreciable_cost ?? ''}
          onChange={(val) => onChange('depreciable_cost', val === '' ? null : Number(val))}
          error={!!errors.depreciable_cost}
        />
      </FormField>

      <FormField
        label="Salvage Value"
        error={errors.salvage_value}
      >
        <NumberInput
          placeholder="0.00"
          prefix="$"
          decimalScale={2}
          description="Expected value at end of useful life"
          value={values.salvage_value ?? ''}
          onChange={(val) => onChange('salvage_value', val === '' ? null : Number(val))}
          error={!!errors.salvage_value}
        />
      </FormField>

      <FormField
        label="Asset Life (months)"
        error={errors.asset_life_months}
        required
      >
        <NumberInput
          placeholder="60"
          min={1}
          description="Useful life in months (e.g., 60 = 5 years)"
          value={values.asset_life_months ?? ''}
          onChange={(val) => onChange('asset_life_months', val === '' ? null : Number(val))}
          error={!!errors.asset_life_months}
        />
      </FormField>

      <FormField
        label="Depreciation Method"
        error={errors.depreciation_method}
        required
      >
        <Select
          placeholder="Select method"
          data={DEPRECIATION_METHODS}
          value={values.depreciation_method}
          onChange={(val) => onChange('depreciation_method', val)}
          error={!!errors.depreciation_method}
        />
      </FormField>

      <FormField
        label="Date Acquired"
        error={errors.date_acquired}
        required
      >
        <DateInput
          description="Date the asset was placed in service"
          valueFormat="YYYY-MM-DD"
          value={values.date_acquired ? new Date(values.date_acquired) : null}
          onChange={(date) => onChange('date_acquired', date?.toISOString().split('T')[0] || null)}
          error={!!errors.date_acquired}
        />
      </FormField>
    </Stack>
  );
}
